import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Trash2, RefreshCw } from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const STATUSES = ["pending", "confirmed", "in_progress", "completed", "cancelled"];
const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-sky-100 text-sky-800",
  in_progress: "bg-brand-lime/20 text-brand-lime-dark",
  completed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-700",
};

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/bookings", { params: filter ? { status: filter } : {} })
      .then((r) => setBookings(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success(`Booking marked ${status.replace("_", " ")}`);
      if (selected?.id === id) setSelected({ ...selected, status });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveNote = async () => {
    try {
      await api.patch(`/admin/bookings/${selected.id}`, { admin_note: note });
      toast.success("Note saved");
      setSelected({ ...selected, admin_note: note });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this booking permanently?")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      toast.success("Booking deleted");
      setSelected(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="admin-bookings-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-brand-emerald">Bookings</h1>
          <p className="text-brand-muted text-sm mt-1">{bookings.length} booking{bookings.length !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={load} data-testid="bookings-refresh-btn" className="p-2.5 rounded-xl bg-white border border-[#D6E4DC] text-brand-emerald hover:bg-brand-mint" aria-label="Refresh"><RefreshCw className="w-4 h-4" /></button>
          {["", ...STATUSES].map((s) => (
            <button
              key={s || "all"}
              data-testid={`bookings-filter-${s || "all"}`}
              onClick={() => setFilter(s)}
              className={`px-4 py-2 rounded-full text-xs font-bold transition-colors ${filter === s ? "bg-brand-emerald text-white" : "bg-white text-brand-muted border border-[#D6E4DC]"}`}
            >
              {s ? s.replace("_", " ") : "All"}
            </button>
          ))}
        </div>
      </div>

      <div className="grid xl:grid-cols-5 gap-6">
        <div className="xl:col-span-3 bg-white rounded-2xl border border-[#D6E4DC] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="bookings-table">
              <thead>
                <tr className="bg-brand-mint text-brand-emerald text-left">
                  <th className="px-4 py-3 font-bold">Reference</th>
                  <th className="px-4 py-3 font-bold">Customer</th>
                  <th className="px-4 py-3 font-bold">Service</th>
                  <th className="px-4 py-3 font-bold">Date</th>
                  <th className="px-4 py-3 font-bold">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-brand-muted">Loading…</td></tr>
                ) : bookings.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-brand-muted" data-testid="bookings-empty">No bookings found.</td></tr>
                ) : bookings.map((b) => (
                  <tr
                    key={b.id}
                    data-testid={`booking-row-${b.reference}`}
                    onClick={() => { setSelected(b); setNote(b.admin_note || ""); }}
                    className={`cursor-pointer border-t border-[#EFF5F1] hover:bg-brand-mint/60 transition-colors ${selected?.id === b.id ? "bg-brand-mint" : ""}`}
                  >
                    <td className="px-4 py-3 font-bold text-brand-emerald">{b.reference}</td>
                    <td className="px-4 py-3">{b.name}<span className="block text-xs text-brand-muted">{b.phone}</span></td>
                    <td className="px-4 py-3 text-xs">{b.service_category}<span className="block text-brand-muted">{b.service_tier}</span></td>
                    <td className="px-4 py-3 text-xs">{b.date}</td>
                    <td className="px-4 py-3"><span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${STATUS_STYLES[b.status] || "bg-gray-100"}`}>{b.status.replace("_", " ")}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="xl:col-span-2">
          {selected ? (
            <div className="bg-white rounded-2xl border border-[#D6E4DC] p-6 sticky top-4" data-testid="booking-detail-panel">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="font-display font-extrabold text-brand-emerald text-lg">{selected.reference}</p>
                  <p className="text-xs text-brand-muted">{new Date(selected.created_at).toLocaleString("en-NG")}</p>
                </div>
                <button onClick={() => remove(selected.id)} data-testid="booking-delete-btn" className="p-2 rounded-lg text-red-500 hover:bg-red-50" aria-label="Delete booking"><Trash2 className="w-4 h-4" /></button>
              </div>
              <dl className="space-y-2.5 text-sm mb-5">
                {[
                  ["Customer", `${selected.name} · ${selected.phone}`],
                  ["Email", selected.email],
                  ["Service", `${selected.service_category} — ${selected.service_tier || "-"}`],
                  ["Property", selected.property_type || "-"],
                  ["Frequency", selected.frequency || "-"],
                  ["Add-ons", selected.addon_details?.length ? selected.addon_details.map((a) => `${a.name}${a.quantity > 1 ? ` × ${a.quantity}` : ""} — ₦${Number(a.subtotal || 0).toLocaleString("en-NG")}`).join("; ") : (selected.addons?.length ? selected.addons.join(", ") : "None")],
                  ["Base Price", selected.base_price != null ? `₦${Number(selected.base_price).toLocaleString("en-NG")}` : "-"],
                  ["Add-ons Total", selected.addons_total != null ? `₦${Number(selected.addons_total).toLocaleString("en-NG")}` : "-"],
                  ["Location", `${selected.area ? selected.area + ", " : ""}${selected.lga}, Lagos`],
                  ["Address", selected.address],
                  ["Landmark", selected.landmark || "-"],
                  ["Schedule", `${selected.date} · ${selected.time_slot}`],
                  ["Estimate", selected.estimated_price || "-"],
                  ["Notes", selected.notes || "-"],
                ].map(([k, v]) => (
                  <div key={k} className="flex gap-3"><dt className="w-20 shrink-0 text-xs font-bold text-brand-muted uppercase pt-0.5">{k}</dt><dd className="text-brand-ink flex-1 break-words">{v}</dd></div>
                ))}
              </dl>
              <label className="block text-xs font-bold text-brand-emerald uppercase tracking-wider mb-1.5">Admin Note</label>
              <textarea data-testid="booking-note-input" value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="w-full px-3 py-2 rounded-xl border border-[#D6E4DC] text-sm focus:outline-none focus:ring-2 focus:ring-brand-lime/50 mb-3" />
              <button onClick={saveNote} data-testid="booking-note-save-btn" className="w-full mb-4 py-2 rounded-xl bg-brand-mint text-brand-emerald text-sm font-bold hover:bg-brand-lime/30 transition-colors">Save Note</button>
              <label className="block text-xs font-bold text-brand-emerald uppercase tracking-wider mb-2">Update Status</label>
              <div className="flex flex-wrap gap-2">
                {STATUSES.map((s) => (
                  <button
                    key={s}
                    data-testid={`booking-status-${s}`}
                    onClick={() => updateStatus(selected.id, s)}
                    className={`px-3.5 py-1.5 rounded-full text-xs font-bold transition-colors ${selected.status === s ? "bg-brand-emerald text-white" : "bg-white border border-[#D6E4DC] text-brand-muted hover:border-brand-emerald"}`}
                  >
                    {s.replace("_", " ")}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-brand-mint rounded-2xl border border-dashed border-brand-emerald/20 p-10 text-center text-brand-muted text-sm" data-testid="booking-detail-empty">
              Select a booking to view full details and manage its status.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
